import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

/**
 * Hint system state (Scope A4). Enforces "one hint open at a time" and
 * "auto-expand on first use, collapse back to the gold tag after."
 */
interface HintCtx {
  openId: string | null;
  toggle: (id: string) => void;
  close: () => void;
  /** Open this hint ONCE — on the first use of the control it explains. No-op after. */
  autoShow: (id: string) => void;
}

const Ctx = createContext<HintCtx | null>(null);

export function HintProvider({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const shownRef = useRef<Set<string>>(new Set());

  const toggle = useCallback((id: string) => setOpenId((cur) => (cur === id ? null : id)), []);
  const close = useCallback(() => setOpenId(null), []);
  const autoShow = useCallback((id: string) => {
    if (shownRef.current.has(id)) return;
    shownRef.current.add(id);
    setOpenId(id);
  }, []);

  return <Ctx.Provider value={{ openId, toggle, close, autoShow }}>{children}</Ctx.Provider>;
}

export function useHints(): HintCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useHints must be used within <HintProvider>");
  return c;
}
